import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { categoryList } from "@/utils/articles";

const primaryLinks = [
  { label: "首页", to: "/", exact: true },
  { label: "关于我们", to: "/about", exact: false },
  { label: "联系我们", to: "/contact", exact: false },
];

export default function SiteHeader() {
  const navigate = useNavigate();
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const drawerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setKeyword(location.pathname === "/search" ? params.get("q") ?? "" : "");
    setDrawerOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = drawerOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [drawerOpen]);

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setDrawerOpen(false);
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, []);

  const submitSearch = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const q = keyword.trim();
    setDrawerOpen(false);
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  const navClass = ({ isActive }: { isActive: boolean }) =>
    `relative py-2 text-sm font-medium transition-colors ${
      isActive ? "text-primary-600" : "text-foreground-800 hover:text-primary-600"
    }`;

  return (
    <header className="sticky top-0 z-40 bg-background-50/95 backdrop-blur border-b border-background-200">
      <div className="hidden md:block bg-primary-800 text-background-50">
        <div className="max-w-portal mx-auto px-4 md:px-6 h-9 flex items-center justify-between text-xs">
          <div className="flex items-center gap-4">
            <span className="font-label tracking-wide">2026 年 10 月 17 日 · 星期六</span>
            <span className="hidden lg:inline text-background-50/70">权威爆料 · 每日实时更新</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="hidden lg:inline text-background-50/70">本站内容均为虚构演示，请理性阅读</span>
            <Link to="/contact" className="hover:text-accent-300 transition-colors">
              线索投稿
            </Link>
          </div>
        </div>
      </div>

      <div className="max-w-portal mx-auto px-4 md:px-6 h-[64px] md:h-[76px] flex items-center gap-3 md:gap-6">
        <Link to="/" className="flex items-center gap-2.5 shrink-0 cursor-pointer">
          <span className="w-10 h-10 md:w-11 md:h-11 rounded-md bg-primary-600 text-background-50 flex items-center justify-center font-label font-bold text-lg md:text-xl tracking-tight">
            51
          </span>
          <span className="leading-tight">
            <span className="block font-heading text-lg md:text-xl font-black text-foreground-950 tracking-wide">
              黑料网
            </span>
            <span className="hidden sm:block text-[10px] tracking-[0.22em] text-foreground-500 font-label">
              B A O L I A O . 5 1
            </span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-7 ml-2">
          {primaryLinks.slice(0, 1).map((link) => (
            <NavLink key={link.to} to={link.to} end={link.exact} className={navClass}>
              {link.label}
            </NavLink>
          ))}
          {categoryList.map((category) => (
            <NavLink key={category.slug} to={`/category/${category.slug}`} className={navClass}>
              {category.navLabel}
            </NavLink>
          ))}
          {primaryLinks.slice(1).map((link) => (
            <NavLink key={link.to} to={link.to} className={navClass}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="ml-auto flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            className="hidden md:flex items-center relative w-[210px] xl:w-[260px]"
            role="search"
          >
            <span className="absolute left-3 w-4 h-4 flex items-center justify-center text-foreground-400">
              <i className="ri-search-line text-sm" aria-hidden="true" />
            </span>
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              name="header_search"
              placeholder="搜索明星、综艺、热点…"
              aria-label="站内搜索"
              className="w-full h-9 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:bg-background-50 transition-colors"
            />
          </form>

          <Link
            to="/contact"
            className="hidden xl:inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium hover:bg-accent-400 transition-colors whitespace-nowrap cursor-pointer"
          >
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-megaphone-line text-sm" aria-hidden="true" />
            </span>
            我要爆料
          </Link>

          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            aria-label="打开导航菜单"
            aria-expanded={drawerOpen}
            className="lg:hidden w-10 h-10 flex items-center justify-center rounded-md border border-background-200 text-foreground-800 hover:bg-background-100 transition-colors cursor-pointer"
          >
            <i className="ri-menu-2-line text-xl" aria-hidden="true" />
          </button>
        </div>
      </div>

      <div
        className={`fixed inset-0 z-50 lg:hidden transition-opacity ${
          drawerOpen ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
        aria-hidden={!drawerOpen}
      >
        <div
          className="absolute inset-0 bg-foreground-950/50"
          onClick={() => setDrawerOpen(false)}
        />
        <div
          ref={drawerRef}
          role="dialog"
          aria-modal="true"
          aria-label="移动端导航"
          className={`absolute right-0 top-0 h-full w-[86%] max-w-[340px] bg-background-50 flex flex-col transition-transform duration-300 ${
            drawerOpen ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="h-16 px-5 flex items-center justify-between border-b border-background-200">
            <span className="font-heading text-lg font-black text-foreground-950">51黑料网</span>
            <button
              type="button"
              onClick={() => setDrawerOpen(false)}
              aria-label="关闭导航菜单"
              className="w-9 h-9 flex items-center justify-center rounded-md text-foreground-700 hover:bg-background-100 transition-colors cursor-pointer"
            >
              <i className="ri-close-line text-xl" aria-hidden="true" />
            </button>
          </div>

          <div className="p-5 overflow-y-auto flex-1">
            <form onSubmit={submitSearch} className="relative mb-6" role="search">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-sm" aria-hidden="true" />
              </span>
              <input
                value={keyword}
                onChange={(event) => setKeyword(event.target.value)}
                type="search"
                name="drawer_search"
                placeholder="搜索关键词"
                aria-label="站内搜索"
                className="w-full h-10 pl-9 pr-3 rounded-full bg-background-100 border border-background-200 text-sm focus:outline-none focus:border-primary-400"
              />
            </form>

            <p className="text-xs font-label tracking-widest text-foreground-400 mb-2">栏目导航</p>
            <div className="flex flex-col">
              <Link to="/" className="py-3 text-[15px] text-foreground-900 border-b border-background-200 flex items-center gap-2">
                <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                  <i className="ri-home-4-line text-base" aria-hidden="true" />
                </span>
                首页
              </Link>
              {categoryList.map((category) => (
                <Link
                  key={category.slug}
                  to={`/category/${category.slug}`}
                  className="py-3 text-[15px] text-foreground-900 border-b border-background-200 flex items-center justify-between"
                >
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 flex items-center justify-center text-primary-500">
                      <i className="ri-article-line text-base" aria-hidden="true" />
                    </span>
                    {category.name}
                  </span>
                  <span className="w-4 h-4 flex items-center justify-center text-foreground-300">
                    <i className="ri-arrow-right-s-line" aria-hidden="true" />
                  </span>
                </Link>
              ))}
              <Link to="/about" className="py-3 text-[15px] text-foreground-900 border-b border-background-200">
                关于我们
              </Link>
              <Link to="/contact" className="py-3 text-[15px] text-foreground-900 border-b border-background-200">
                联系我们
              </Link>
            </div>

            <Link
              to="/contact"
              className="mt-6 flex items-center justify-center gap-2 h-10 rounded-md bg-accent-500 text-foreground-950 text-sm font-medium"
            >
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-megaphone-line" aria-hidden="true" />
              </span>
              投稿爆料线索
            </Link>
            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              本站为演示站点，全部人物与事件均为虚构，如有雷同纯属巧合。
            </p>
          </div>
        </div>
      </div>
    </header>
  );
}